<script lang="ts">
  import { fly } from 'svelte/transition';

  interface Props {
    type?: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
    dismissible?: boolean;
    ondismiss?: () => void;
  }

  const {
    type = 'info',
    message,
    duration = 5000,
    dismissible = true,
    ondismiss,
  }: Props = $props();

  let visible = $state(true);
  let timeoutId: ReturnType<typeof setTimeout>;

  // Auto-dismiss after duration using $effect
  $effect(() => {
    if (duration > 0) {
      timeoutId = setTimeout(() => {
        dismiss();
      }, duration);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  });

  function dismiss() {
    visible = false;
    ondismiss?.();
  }

  // Toast styles based on type using $derived
  const toastStyles = $derived(
    {
      success: 'bg-green-50 border-green-200 text-green-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800',
    }[type]
  );

  const iconStyles = $derived(
    {
      success: 'text-green-400',
      error: 'text-red-400',
      warning: 'text-yellow-400',
      info: 'text-blue-400',
    }[type]
  );

  // Icons for each type
  const iconPath = $derived(
    {
      success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
      warning:
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
      info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    }[type]
  );
</script>

{#if visible}
  <div class="fixed top-4 right-4 z-[100] max-w-sm w-full" transition:fly={{ x: 300, duration: 300 }}>
    <div class="rounded-lg border p-4 shadow-lg {toastStyles}">
      <div class="flex items-start">
        <!-- Icon -->
        <div class="shrink-0">
          <svg class="h-5 w-5 {iconStyles}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={iconPath} />
          </svg>
        </div>

        <!-- Message -->
        <div class="ml-3 flex-1">
          <p class="text-sm font-medium">
            {message}
          </p>
        </div>

        <!-- Dismiss button -->
        {#if dismissible}
          <div class="ml-4 shrink-0">
            <button
              type="button"
              class="inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 hover:opacity-75 {iconStyles}"
              onclick={dismiss}
            >
              <span class="sr-only">Close</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        {/if}
      </div>

      <!-- Progress bar for auto-dismiss -->
      {#if duration > 0}
        <div class="mt-2 w-full bg-white/20 rounded-full h-1">
          <div
            class="bg-current h-1 rounded-full transition-all ease-linear"
            style="width: 100%; animation: shrink {duration}ms linear forwards;"
          ></div>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  @keyframes shrink {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
</style>
